import { useCallback } from 'react';

interface HasId {
  _id: string;
}

interface UseSelectNextItemParams<T extends HasId> {
  items: T[];
  setActive: (id: string | undefined) => void;
  onNoItemsLeft: () => void;
  onSelectItem?: (item: T) => void;
}

interface UseSelectNextItemResult {
  selectNextItem: (currentItemId: string) => void;
}

export function useSelectNextItem<T extends HasId>({
  items,
  setActive,
  onNoItemsLeft,
  onSelectItem,
}: UseSelectNextItemParams<T>): UseSelectNextItemResult {
  const selectNextItem = useCallback(
    (currentItemId: string) => {
      const currentIndex = items.findIndex((item) => item._id === currentItemId);
      if (currentIndex === -1) return;

      const prevIndex = currentIndex - 1;
      const nextIndex = currentIndex + 1;
      const nextItem = items[nextIndex] ?? items[prevIndex];

      if (!nextItem) {
        setActive(undefined);
        onNoItemsLeft();
      } else {
        setActive(nextItem._id);
        onSelectItem?.(nextItem);
      }
    },
    [items, setActive, onNoItemsLeft, onSelectItem]
  );

  return { selectNextItem };
}
